import { MarcheAlerte } from './marche-alerte.model';
import { MarcheBpu } from './marche-bpu.model';
import { MarcheDocument } from './marche-document.model';
import { MarcheResponsable } from './marche-responsable.model';
import { MarcheTraitant } from './marche-traitant.model';
import { MarcheReconduction } from './marche-reconduction.model';
import { MarcheAmelioration } from './marche-amelioration.model';
import { DateUtils } from '../services/date-utils';

export class Marche {
  constructor(
    public uuid: string,
    public referenceAccordCadre: string,
    public nom: string,
    public type: string,
    public typeReconduction: string,
    public dateDebut: Date | null,
    public dateFin: Date | null,
    public responsablesTechniques: MarcheResponsable[],
    public responsableAdministratif: string,
    public refCmp: string,
    public traitants: MarcheTraitant[],
    public seuilMinimum: number,
    public seuilMaximum: number,
    public catalogue: MarcheDocument | null,
    public bpuDocument: MarcheDocument | null,
    public annexes: MarcheDocument[],
    public reconductions: MarcheReconduction[],
    public avenants: MarcheDocument[],
    public ameliorations: MarcheAmelioration[],
    public bpus: MarcheBpu[] = [],
    public alerte?: MarcheAlerte | null,
    public montantEngage?: number,
    public montantEngagePrev?: number,
    public dateCreation?: Date | null,
    public dateModification?: Date | null,
    public createur?: string | null,
    public modificateur?: string | null,
    public champsObligatoiresManquants?: boolean,
    public isSupprimable?: boolean
  ) {}

  private static parsePhpDate(value: any): Date | null {
    if (!value) return null;

    if (typeof value === 'string') return new Date(value);

    if (typeof value === 'object' && value.date) {
      const iso = value.date.replace(' ', 'T').split('.')[0];
      return new Date(iso);
    }

    return null;
  }

  static adapt(item: any): Marche {
    return new Marche(
      item.uuid ?? '',
      item.referenceAccordDate ?? '',
      item.nom ?? '',
      item.type ?? '',
      item.typeReconduction ?? '',
      Marche.parsePhpDate(item.dateDebut),
      Marche.parsePhpDate(item.dateFin),
      (item.responsablesTechniques || []).map((r: any) => MarcheResponsable.adapt(r)),
      item.responsableAdministratif ?? '',
      item.refCmp ?? '',
      (item.traitants || []).map((t: any) => MarcheTraitant.adapt(t)),
      item.seuilMinimum ?? 0,
      item.seuilMaximum ?? 0,
      item.catalogue ? MarcheDocument.adapt(item.catalogue) : null,
      item.bpuDocument ? MarcheDocument.adapt(item.bpuDocument) : null,
      (item.annexes || []).map((a: any) => MarcheDocument.adapt(a)),
      (item.reconductions || []).map((r: any) => MarcheReconduction.adapt(r)),
      (item.avenants || []).map((a: any) => MarcheDocument.adapt(a)),
      (item.ameliorations || []).map((a: any) => MarcheAmelioration.adapt(a)),
      (item.uos || []).map((b: any) => MarcheBpu.adapt(b)),
      item.alerte ? MarcheAlerte.adapt(item.alerte) : null,
      item.consommation ?? 0,
      item.consommationPrev ?? 0,
      Marche.parsePhpDate(item.dateCreation),
      Marche.parsePhpDate(item.dateModification),
      item.createur ?? null,
      item.modificateur ?? null,
      item.champsObligatoiresManquants ?? false,
      item.isSupprimable ?? false
    );
  }

  static adaptToApi(item: any): any {
    return {
      uuid: item.uuid ?? null,
      referenceAccordDate: item.referenceAccordCadre,
      nom: item.nom,
      type: item.type && item.type !== '' ? item.type : null,
      typeReconduction: item.typeReconduction && item.typeReconduction !== '' ? item.typeReconduction : null,
      dateDebut: item.dateDebut instanceof Date && !isNaN(item.dateDebut.getTime()) ? DateUtils.localDateToApiString(item.dateDebut) : null,
      dateFin: item.dateFin instanceof Date && !isNaN(item.dateFin.getTime()) ? DateUtils.localDateToApiString(item.dateFin) : null,
      responsableAdministratif: item.responsableAdministratif ?? null,
      refCmp: item.refCmp ?? null,
      traitants: (item.traitants || []).map((t: MarcheTraitant) => MarcheTraitant.adaptToApi(t)),
      seuilMinimum: item.seuilMinimum ?? null,
      seuilMaximum: item.seuilMaximum ?? null,
      catalogue: item.catalogue ? MarcheDocument.adapt(item.catalogue) : null,
      bpuDocument: item.bpuDocument ? MarcheDocument.adapt(item.bpuDocument) : null,
      annexes: (item.annexes || []).map((a: MarcheDocument) => MarcheDocument.adapt(a)),
      modalites: null,
      penalites: null,
      allotissement: null,
      uos: (item.bpus || []).map((b: MarcheBpu) => MarcheBpu.adaptToApi(b)),
      alerte: item.alerte,
      dateCreation: item.dateCreation,
      dateModification: item.dateModification,
      createur: item.createur ?? null,
      modificateur: item.modificateur ?? null
    };
  }
}
